import {
  type Child,
  type ElNode,
  type Instruction,
  isString,
  type MaybeDynamic,
  raw,
  reader,
  type Reader,
  safeHtml,
} from '@crown/framework/ir';
import type { BrandColor } from '../types';
import iconTemplate from './empty-state-icon.tsrx';
import template from './empty-state.tsrx';

import './empty-state.css';

export type EmptyStateVariant = 'default' | 'compact' | 'minimal';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
  variant?: BrandColor | 'primary' | 'secondary' | 'ghost';
}

export interface EmptyStateProps {
  title: MaybeDynamic<string>;
  description?: MaybeDynamic<string | undefined>;
  /** One of `EmptyStateIcons`, an element, inline SVG markup, or a glyph. */
  icon?: string | HTMLElement | Instruction;
  illustration?: string | HTMLElement;
  actions?: EmptyStateAction[];
  variant?: EmptyStateVariant;
  iconVariant?: BrandColor;
  extraClasses?: string | string[];
  /**
   * The heading level the title takes, 1 to 6.
   *
   * An empty state usually stands for a section of the page, so it sits one
   * below the page's own heading. It was fixed at 3, which skipped a level on
   * every page whose only other heading is the h1 - a reader listing headings
   * finds a gap where the missing level was. Pass the level this one belongs
   * under where the surrounding structure says otherwise.
   */
  headingLevel?: number;
}

const EmptyStateTemplate: (props: EmptyStateTemplateProps) => Instruction =
  template;

/** The icon block: its classes, the inline size a plain glyph needs, and its content. */
export interface EmptyStateIcon {
  readonly classes: string;
  readonly style: string | null;
  readonly content: Child;
}

/** The illustration: an image by its address, or content of its own. */
export interface EmptyStateIllustration {
  /** The image's address; null when the illustration is content of its own. */
  readonly image: string | null;
  readonly content: Child;
}

/** An action as a button: its classes, what it says and what it does. */
export interface EmptyStateButton {
  readonly key: string;
  readonly classes: string;
  readonly label: string;
  readonly onClick: () => void;
}

export interface EmptyStateTemplateProps {
  readonly classes: string;
  readonly illustration: EmptyStateIllustration | undefined;
  readonly icon: EmptyStateIcon | undefined;
  /** 1 to 6. */
  readonly headingLevel: number;
  readonly title: Reader<string>;
  readonly description: Reader<string | undefined>;
  readonly actions: readonly EmptyStateButton[];
}

function containerClasses({
  variant = 'default',
  extraClasses,
}: EmptyStateProps): string {
  const classes = ['empty-state'];
  if (variant !== 'default') classes.push(variant);
  if (extraClasses)
    classes.push(
      ...(Array.isArray(extraClasses) ? extraClasses : [extraClasses]),
    );
  return classes.join(' ');
}

/** An element is adopted, inline markup is its own, and anything else is an image source. */
function illustrationOf(
  illustration: EmptyStateProps['illustration'],
): EmptyStateIllustration | undefined {
  if (illustration === undefined) return undefined;
  if (!isString(illustration)) return { image: null, content: illustration };
  if (illustration.startsWith('<svg') || illustration.startsWith('<img')) {
    return { image: null, content: raw(safeHtml(illustration)) };
  }
  return { image: illustration, content: null };
}

/** An element, an instruction or inline SVG is the icon itself; anything else is a glyph, which needs a size. */
function iconBlock(props: EmptyStateProps): EmptyStateIcon | undefined {
  const { icon, iconVariant, illustration } = props;
  if (icon === undefined || illustration !== undefined) return undefined;
  const classes = iconVariant
    ? `empty-state-icon ${iconVariant}`
    : 'empty-state-icon';
  if (!isString(icon)) return { classes, style: null, content: icon };
  if (icon.startsWith('<svg'))
    return { classes, style: null, content: raw(safeHtml(icon)) };
  return { classes: `${classes} text-3xl`, style: null, content: icon };
}

/** The heading level, within 1 to 6; anything else is the default. */
function headingLevelOf(headingLevel: number): number {
  return Number.isInteger(headingLevel) &&
    headingLevel >= 1 &&
    headingLevel <= 6
    ? headingLevel
    : 2;
}

function actionButtons(
  actions: readonly EmptyStateAction[],
): EmptyStateButton[] {
  return actions.map((action, index) => ({
    key: String(index),
    classes: `btn ${action.variant ?? 'primary'}`,
    label: action.label,
    onClick: action.onClick,
  }));
}

export function EmptyState(props: EmptyStateProps): ElNode {
  const node = EmptyStateTemplate({
    classes: containerClasses(props),
    illustration: illustrationOf(props.illustration),
    icon: iconBlock(props),
    headingLevel: headingLevelOf(props.headingLevel ?? 2),
    title: reader(props.title),
    description: reader(props.description),
    actions: actionButtons(props.actions ?? []),
  });
  if (node.t !== 'el') throw new Error('EmptyState renders one element');
  return node;
}

/** The built-in icons, by name. */
export type EmptyStateIconName =
  | 'folder'
  | 'document'
  | 'search'
  | 'inbox'
  | 'list'
  | 'users'
  | 'announcement'
  | 'menu'
  | 'plus'
  | 'shield'
  | 'layers'
  | 'layout'
  | 'branch'
  | 'clipboard'
  | 'puzzle'
  | 'location';

const EmptyStateIconTemplate: (props: {
  readonly name: EmptyStateIconName;
}) => Instruction = iconTemplate;

/** The built-in icons, each drawn in the current text colour; pass one as `icon`. */
export const EmptyStateIcons = {
  folder: EmptyStateIconTemplate({ name: 'folder' }),
  document: EmptyStateIconTemplate({ name: 'document' }),
  search: EmptyStateIconTemplate({ name: 'search' }),
  inbox: EmptyStateIconTemplate({ name: 'inbox' }),
  list: EmptyStateIconTemplate({ name: 'list' }),
  users: EmptyStateIconTemplate({ name: 'users' }),
  announcement: EmptyStateIconTemplate({ name: 'announcement' }),
  menu: EmptyStateIconTemplate({ name: 'menu' }),
  plus: EmptyStateIconTemplate({ name: 'plus' }),
  shield: EmptyStateIconTemplate({ name: 'shield' }),
  layers: EmptyStateIconTemplate({ name: 'layers' }),
  layout: EmptyStateIconTemplate({ name: 'layout' }),
  branch: EmptyStateIconTemplate({ name: 'branch' }),
  clipboard: EmptyStateIconTemplate({ name: 'clipboard' }),
  puzzle: EmptyStateIconTemplate({ name: 'puzzle' }),
  location: EmptyStateIconTemplate({ name: 'location' }),
} satisfies Record<EmptyStateIconName, Instruction>;
